import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Select, Tooltip } from 'antd'
import { ApexOptions } from 'apexcharts'
import { TOKEN_ICON_MAPPING, TOKEN_WITHDRAW } from 'config/constants'
import { useTranslation } from 'contexts/Localization'
import useTokenCoinGeckoPrice from 'hooks/useTokenCoinGeckoPrice'
import moment from 'moment-timezone'
import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'state'
import getWalletReport from 'state/wallet/fetch/getWalletReport'
import styled from 'styled-components'
import { addCommasToNumber } from 'utils'
import { formatAmountOrigin } from 'utils/formatInfoNumbers'
import { ArrowBackIcon } from 'widgets'
import { getPriceMBCOrigin } from 'state/nfts/api'

const ReactApexChart = dynamic(() => import('react-apexcharts'), { ssr: false })

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 24px;
  margin-bottom: 32px;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 1fr 1fr;
  }
`

const BoxWrapper = styled.div`
  padding: 24px 16px;

  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 24px;
  }

  .ant-select-selector {
    padding: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }

  .token {
    display: grid;
    grid-gap: 16px;

    p {
      font-size: 14px;
      font-weight: 700;
      line-height: 1.4;

      ${({ theme }) => theme.mediaQueries.lg} {
        font-size: 18px;
      }

      &.value {
        text-align: right;
      }

      &.rate {
        color: #989996;
        font-size: 12px;
        font-weight: 400;
        text-align: right;

        ${({ theme }) => theme.mediaQueries.lg} {
          font-size: 14px;
        }
      }
    }

    .header {
      padding-bottom: 16px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
  }
`

const timeOptions = (t) => [
  {
    label: t('in 7 days'),
    value: 7,
  },
  {
    label: t('in 30 days'),
    value: 30,
  },
  {
    label: t('in 90 days'),
    value: 90,
  },
]

const BalanceOverview = () => {
  const [mbcPrice, setMbcPrice] = useState<any>(null)
  useEffect(() => {
    getPriceMBCOrigin().then((res) => {
      setMbcPrice(res.price)
    })
  }, [])
  const { t } = useTranslation()
  const { balances, opvPrice, walletSummary } = useSelector((state: AppState) => state.wallet)

  const usdtPrice = useTokenCoinGeckoPrice('USDT')

  // Set State
  const [paramChartData, setParamChartData] = useState({
    range: 7,
    type: 'CHART',
  })
  const [paramRateTotalBalance, setParamRateTotalBalance] = useState({
    range: 7,
    type: 'TOTAL_BALANCE',
  })
  const [chartBalanceData, setChartBalanceData] = useState<any>(undefined)
  const [rateTotalBalance, setRateTotalBalance] = useState(0)

  const { balanceData, totalBalance } = useMemo(() => {
    const usdtAvailableBalance = balances
      ?.filter((item) => ['USDT', 'PROFIT', 'PROFIT_NODE_BALANCE', 'PROFIT_MOON']?.includes(item?.tokenSymbol))
      ?.reduce(
        (total, cur) => total + cur?.available,

        0,
      )

    const usdtLockingBalance = balances
      ?.filter((item) => item?.tokenSymbol === 'PROFIT')
      ?.reduce((prev, next) => prev + next.locking, 0)
    const opvAvailableBalance = balances?.find((item) => item?.tokenSymbol === 'OPV')?.available
    const opvLockingBalance = balances?.find((item) => item?.tokenSymbol === 'OPV')?.locking

    return {
      balanceData: [
        {
          token: 'USDT',
          total: usdtAvailableBalance + usdtLockingBalance,
          totalUsd: (usdtAvailableBalance + usdtLockingBalance) * (usdtPrice?.price || 1),
          detail: [
            {
              title: 'Available',
              tooltip: 'Amount of USDT available for withdrawal',
              balance: usdtAvailableBalance,
              balanceUsd: usdtAvailableBalance * (usdtPrice?.price || 1),
            },
            // {
            //   title: 'Locked',
            //   tooltip: 'Amount of USDT unavailable for withdrawal',
            //   balance: usdtLockingBalance,
            //   balanceUsd: usdtLockingBalance * (usdtPrice?.price || 1),
            // },
          ],
        },
        {
          token: 'OPV',
          total: opvAvailableBalance + opvLockingBalance,
          totalUsd: (opvAvailableBalance + opvLockingBalance) * mbcPrice * (usdtPrice?.price || 1),
          detail: [
            {
              title: 'Available',
              tooltip: 'Amount of MBC available for withdrawal',
              balance: opvAvailableBalance,
              balanceUsd: opvAvailableBalance * mbcPrice * (usdtPrice?.price || 1),
            },
            // {
            //   title: 'Locked',
            //   tooltip: 'Amount of MBC unavailable for withdrawal',
            //   balance: opvLockingBalance,
            //   balanceUsd: opvLockingBalance * mbcPrice * (usdtPrice?.price || 1),
            // },
          ],
        },
      ],
      totalBalance:
        usdtLockingBalance + usdtAvailableBalance + opvAvailableBalance * mbcPrice + opvLockingBalance * mbcPrice,
    }
  }, [balances, mbcPrice, usdtPrice?.price])

  // Chart Options
  const chartData = {
    series: [
      {
        name: 'MBC',
        data: chartBalanceData?.map((item) => +item?.amount),
        color: '#00349D',
        style: {
          stroke: '#00349D',
          strokeWidth: 2,
          fill: {
            type: 'linear',
            stops: [
              {
                offset: 0,
                color: '#00349D',
              },
              {
                offset: 1,
                color: 'rgba(173, 247, 0, 0)',
              },
            ],
          },
        },
      },
    ],
  }

  const options: ApexOptions = {
    chart: {
      type: 'line',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      width: '100%',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    title: {
      text: '',
      align: 'left',
    },
    grid: {
      borderColor: '#0D0F071A',
      row: {
        colors: ['transparent', 'transparent'],
      },
    },
    xaxis: {
      labels: {
        style: {
          colors: '#989996',
        },
      },
      categories: chartBalanceData?.map((item) => moment(item?.formatDate).format('DD/MM')),
      max: paramChartData?.range,
      tickAmount: 5,
    },
    yaxis: {
      labels: {
        style: {
          colors: '#989996',
        },
        formatter(val) {
          return formatAmountOrigin(val)
        },
      },
    },
    tooltip: {
      theme: 'dark',
    },
  }

  useEffect(() => {
    getWalletReport(paramChartData, (res) => setChartBalanceData(res))
    getWalletReport(paramRateTotalBalance, (res) => setRateTotalBalance(+res?.rate))
  }, [, paramChartData.range, paramRateTotalBalance.range])

  return (
    <Wrapper>
      <BoxWrapper className="row-start-1 row-end-3 box-primary">
        <div className="grid space-y-1">
          <p className="text-[18px] text-secondary leading-6">{t('Total balance')}</p>
          <p className="text-[27px] leading-[1.5] font-bold text-text-brand">${addCommasToNumber(totalBalance)}</p>

          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <ArrowBackIcon
                className={`${rateTotalBalance >= 0 ? 'rotate-[90deg]' : 'rotate-[-90deg]'} w-4`}
                fill={rateTotalBalance >= 0 ? '#3AB500' : '#FF0F00'}
              />
              <p
                className={`${rateTotalBalance >= 0 ? 'text-text-success' : 'text-text-danger'} text-[14px] leading-6`}
              >
                {rateTotalBalance}%
              </p>
            </div>

            <Select
              className="w-[120px]"
              options={timeOptions(t)}
              defaultValue={7}
              onChange={(val) => {
                setParamChartData((prev) => ({ ...prev, range: val }))
                setParamRateTotalBalance((prev) => ({ ...prev, range: val }))
              }}
            />
          </div>

          {chartData.series.length > 0 && (
            <ReactApexChart options={options} series={chartData.series} type="line" height="100%" width="100%" />
          )}
        </div>
      </BoxWrapper>

      {balanceData?.map((item) => (
        <BoxWrapper className="box-primary" key={item?.token}>
          <div className="token">
            <div className="header border-b border-solid stroke-sm">
              <div className="flex gap-1 items-center">
                <img className="w-7 bg-teal rounded-full" src={TOKEN_ICON_MAPPING?.[item?.token]} alt="" />
                <p className="text-primary">{TOKEN_WITHDRAW?.[item?.token]}</p>
              </div>

              <div className="flex md:flex-row flex-col gap-1 items-center">
                <p className="value text-primary">{addCommasToNumber(item?.total)}</p>
                <p className="rate text-secondary">≈${addCommasToNumber(item?.totalUsd)}</p>
              </div>
            </div>

            {item?.detail.map((x) => (
              <div className="flex justify-between items-start" key={x?.title}>
                <div className="flex gap-2 items-center">
                  <p className="text-primary">{t(x?.title)}</p>
                  <Tooltip title={t(x?.tooltip)}>
                    <ExclamationCircleOutlined className="text-secondary w-[16px] h-[16px]" rev={undefined} />
                  </Tooltip>
                </div>

                <div className="flex gap-1 items-center md:flex-row flex-col">
                  <p className="value text-primary">{addCommasToNumber(x?.balance)}</p>
                  <p className="rate text-secondary">≈${addCommasToNumber(x?.balanceUsd)}</p>
                </div>
              </div>
            ))}
          </div>
        </BoxWrapper>
      ))}
    </Wrapper>
  )
}

export default BalanceOverview
